import { useCallback, useEffect, useMemo, useState } from 'react';
import { aiEngineeringAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

const AI_DISABLED_DETAIL = 'AI Engineering indisponível';
let cachedAccessState = null;

const resolveBlockedState = (error) => {
  const status = error?.response?.status;
  const detail = String(error?.response?.data?.detail || '').trim();

  if (status === 404 && detail === AI_DISABLED_DETAIL) {
    return { available: false, reason: 'feature_flag_off', detail };
  }

  if (status === 403) {
    return { available: false, reason: 'role_restricted', detail: detail || 'Acesso restrito ao módulo AI Engineering' };
  }

  return { available: false, reason: 'unexpected_error', detail: detail || 'Erro ao validar disponibilidade do módulo' };
};

export const useAIEngineeringAccess = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const [state, setState] = useState({
    loading: true,
    available: false,
    reason: 'idle',
    detail: '',
  });

  const isAdminFamily = useMemo(
    () => ['admin', 'super_admin'].includes(String(user?.role || '').trim().toLowerCase()),
    [user?.role],
  );

  const refresh = useCallback(async () => {
    if (!userId) {
      const nextState = { loading: false, available: false, reason: 'no_session', detail: '' };
      cachedAccessState = nextState;
      setState(nextState);
      return nextState;
    }

    if (!isAdminFamily) {
      const nextState = { loading: false, available: false, reason: 'role_restricted', detail: 'Acesso restrito ao módulo AI Engineering' };
      cachedAccessState = nextState;
      setState(nextState);
      return nextState;
    }

    if (cachedAccessState && cachedAccessState.loading === false) {
      setState(cachedAccessState);
      return cachedAccessState;
    }

    setState((current) => ({ ...current, loading: true }));

    try {
      const featureFlagResponse = await aiEngineeringAPI.getFeatureFlag();
      const enabled = Boolean(featureFlagResponse?.data?.enabled);
      if (!enabled) {
        const nextState = { loading: false, available: false, reason: 'feature_flag_off', detail: AI_DISABLED_DETAIL };
        cachedAccessState = nextState;
        setState(nextState);
        return nextState;
      }

      await aiEngineeringAPI.getControlPlane();
      const nextState = { loading: false, available: true, reason: 'ready', detail: '' };
      cachedAccessState = nextState;
      setState(nextState);
      return nextState;
    } catch (error) {
      const nextState = { loading: false, ...resolveBlockedState(error) };
      cachedAccessState = nextState;
      setState(nextState);
      return nextState;
    }
  }, [isAdminFamily, userId]);

  useEffect(() => {
    refresh().catch(() => null);
  }, [refresh]);

  return {
    ...state,
    isAdminFamily,
    refresh,
  };
};